/**
 * Phone display helpers for the admin dashboard.
 *
 * The invitations list is an operator screen that may stay open on a shared
 * monitor, so a pending invitation shows a masked number: enough to recognise the
 * number just typed (country code + last four), not a readable phone directory.
 *
 * Display only — never use these for identity, matching, or anything sent back
 * to the server. The backend owns normalization (phone_normalize.phone_key).
 */

import { localization, type Translate } from "@/app/localization";

/** Mask an E.164 number for display: "+15551234567" → "+1 ••• ••• 4567".
 *
 *  A value that is not E.164-shaped (legacy rows predating invite-time
 *  validation) still falls back to last-four masking, so an unexpected shape
 *  never leaks the number. */
export function maskPhone(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  if (!s) return "—";

  const digits = s.replace(/\D/g, "");
  if (digits.length <= 4) return "•".repeat(Math.max(digits.length, 2));

  const last4 = digits.slice(-4);
  if (s.startsWith("+")) {
    // Country code = whatever precedes the 10-digit national number, capped so a
    // malformed value can't render an absurd prefix.
    const ccLen = Math.max(0, Math.min(digits.length - 10, 3));
    const cc = ccLen > 0 ? `+${digits.slice(0, ccLen)} ` : "+";
    return `${cc}••• ••• ${last4}`;
  }
  return `••• ••• ${last4}`;
}

const INVITE_SMS_REASONS = new Set([
  "channel_disabled",
  "registration_disabled",
  "invalid_destination",
  "country_blocked",
  "suppressed",
  "suppression_unavailable",
  "not_configured",
  "destination_limit",
  "global_limit",
  "send_failed",
]);

/** Human phrasing for an invite-SMS reason code. The backend decides; this only
 *  says it in words an operator can act on. Callers rendering it pass their `t`;
 *  without one it uses the runtime's active catalog at call time. */
export function inviteSmsReasonText(reason: string | null | undefined, t: Translate = localization.t): string {
  return t("operator.auth.invite_sms_reason", { reason: reason && INVITE_SMS_REASONS.has(reason) ? reason : "other" });
}
